/**
 * User Management Tab Component for Settings page
 */
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../ui/dropdown-menu';
import { Users, UserPlus, MoreVertical, Shield, Key, Trash2 } from 'lucide-react';

// Role styles with updated RBAC roles
const ROLE_STYLES = {
  admin: 'bg-red-500/15 text-red-400 border-red-500/20',
  team_leader: 'bg-blue-500/15 text-blue-400 border-blue-500/20',
  ops_coordinator: 'bg-[#FFC04D]/15 text-[#FFC04D] border-[#FFC04D]/20',
  standard: 'bg-[#FFC04D]/15 text-[#FFC04D] border-[#FFC04D]/20', // Backwards compatibility
  medic: 'bg-[#1ABC9C]/15 text-[#1ABC9C] border-[#1ABC9C]/20',
};

// Role display labels with updated RBAC roles
const ROLE_LABELS = {
  admin: 'Admin',
  team_leader: 'Team Leader',
  ops_coordinator: 'Ops Coordinator',
  standard: 'Ops Coordinator', // Backwards compatibility display
  medic: 'Medic',
};

export function UserManagementTab({
  users,
  currentUser,
  onAddUser,
  onEditRole,
  onResetPassword,
  onDeleteUser
}) {
  return (
    <Card className="bg-[#2B2B2B] border-white/5">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-[#F2F2F2]">Users</CardTitle>
        <Button
          className="bg-[#1ABC9C] hover:bg-[#1ABC9C]/90"
          onClick={onAddUser}
          data-testid="add-user-btn"
        >
          <UserPlus className="w-4 h-4 mr-2" />
          Add User
        </Button>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow className="border-white/5 hover:bg-transparent">
              <TableHead className="text-[#A1A1AA]">Name</TableHead>
              <TableHead className="text-[#A1A1AA]">Email</TableHead>
              <TableHead className="text-[#A1A1AA]">Role</TableHead>
              <TableHead className="text-[#A1A1AA] text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {users.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} className="text-center text-[#A1A1AA] py-8">
                  No users found
                </TableCell>
              </TableRow>
            ) : (
              users.map((user) => (
                <TableRow key={user.id} className="border-white/5 hover:bg-white/5" data-testid={`user-row-${user.id}`}>
                  <TableCell className="text-[#F2F2F2] font-medium">
                    {user.name}
                    {user.id === currentUser?.id && (
                      <Badge className="ml-2 bg-[#1ABC9C]/15 text-[#1ABC9C] border-0">You</Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-[#A1A1AA]">{user.email}</TableCell>
                  <TableCell>
                    <Badge className={ROLE_STYLES[user.role]}>
                      {ROLE_LABELS[user.role] || user.role}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="text-[#A1A1AA] hover:text-[#F2F2F2]">
                          <MoreVertical className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="bg-[#2B2B2B] border-white/10">
                        <DropdownMenuItem onClick={() => onEditRole(user)} className="text-[#F2F2F2] focus:bg-white/10">
                          <Shield className="w-4 h-4 mr-2" />
                          Change Role
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => onResetPassword(user)} className="text-[#F2F2F2] focus:bg-white/10">
                          <Key className="w-4 h-4 mr-2" />
                          Reset Password
                        </DropdownMenuItem>
                        {user.id !== currentUser?.id && (
                          <DropdownMenuItem onClick={() => onDeleteUser(user.id)} className="text-red-400 focus:bg-white/10">
                            <Trash2 className="w-4 h-4 mr-2" />
                            Delete User
                          </DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

export { ROLE_STYLES, ROLE_LABELS };
